import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, input, viewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { delay, filter, map, repeat, switchMap } from 'rxjs/operators';
import { fromMutationObserver } from '@core';
import { of } from 'rxjs';

@Component({
  selector: 'app-text',
  templateUrl: './text.component.html',
  styleUrl: './text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextComponent {

  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly innerElement = viewChild<ElementRef<HTMLDivElement>>('inner');
  private readonly translate = inject(TranslateService);

  private readonly marquee = input;

  protected readonly currentLanguage = toSignal(this.translate.onLangChange.pipe(
    map((event) => {
      return event.lang;
    }),
  ), { initialValue: this.translate.getCurrentLang() });

  private readonly innerElement$ = toObservable(this.innerElement);

  constructor() {
    this.innerElement$.pipe(
      map((elementRef) => elementRef),
      filter((elementRef) => !!elementRef),
      delay(2000),
      map((elementRef) => elementRef.nativeElement),
      switchMap((element) => of(element).pipe(
        repeat({
          delay: () => fromMutationObserver(element, {
            childList: true,
            subtree: true,
            characterData: true,
          }),
        }),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      const speed = 60;
      const element = this.elementRef.nativeElement;
      const scrollWidth = element.scrollWidth;
      const clientWidth = element.clientWidth;

      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      if (scrollWidth > clientWidth) {
        const distance = scrollWidth + clientWidth;
        const duration = distance / speed;
        element.classList.add('marquee-horizontal');
        element.style.setProperty('--marquee-duration', `${ duration }s`);
      }
      else if (scrollHeight - clientHeight > 4) {
        const distance = scrollHeight + clientHeight;
        const duration = distance / speed;
        element.classList.add('marquee-vertical');
        element.style.setProperty('--marquee-duration', `${ duration }s`);
      }
      else {
        element.classList.remove('marquee-horizontal', 'marquee-vertical');
        element.style.removeProperty('--marquee-duration');
      }
    });
  }

}
